<template>
    <div>
        <l-card-headline class="tw-mb-8">
            <template>
                {{ $t("teamDashboard.title") }}
            </template>
        </l-card-headline>

        <div class="tw-grid tw-grid-cols-12 tw-gap-12">
            <div class="tw-col-span-8">
                <l-goals-preview-team v-if="currentUser.rights.G_VIEW" />
                <l-reviews-preview-team v-if="currentUser.rights.R_VIEW" />
                <l-feedback-analytics-preview-team v-if="currentUser.rights.F_VIEW" />
                <l-review-analytics-preview-team v-if="currentUser.rights.R_VIEW" />
            </div>
            <div class="tw-col-span-4">
                <l-notifications-preview-team />
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/api";
import { mapGetters } from "vuex";

import LCardHeadline from "@/components/LCardHeadline";
import LNotificationsPreviewTeam from "./LNotificationsPreviewTeam.vue";
import LGoalsPreviewTeam from "./LGoalsPreviewTeam.vue";
import LReviewsPreviewTeam from "./LReviewsPreviewTeam.vue";
import LFeedbackAnalyticsPreviewTeam from "./LFeedbackAnalyticsPreviewTeam.vue";
import LReviewAnalyticsPreviewTeam from "./LReviewAnalyticsPreviewTeam.vue";

export default {
    components: {
        LCardHeadline,
        LNotificationsPreviewTeam,
        LGoalsPreviewTeam,
        LReviewsPreviewTeam,
        LFeedbackAnalyticsPreviewTeam,
        LReviewAnalyticsPreviewTeam,
    },
    props: {
        user: {
            type: Object,
            default: () => ({}),
        },
        isVisitor: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            isLoading: true,
            selectedTab: {
                id: "question-radar",
            },
            report: {},
            reviewCycles: [],
            form: {
                reportSource: "",
            },
            isSkillHover: false,
        };
    },
    computed: {
        ...mapGetters({
            currentUser: "users/current/user",
            rights: "users/current/rights",
        }),
        tabs() {
            return [
                {
                    id: "question-radar",
                    label: this.$t("reviews.admin.btnShowQuestion"),
                    iconClass: "fal fa-chart-pie",
                },
                {
                    id: "question-horizontalBar",
                    label: this.$t("reviews.admin.btnShowQuestion"),
                    iconClass: "fal fa-chart-bar",
                },
                {
                    id: "category-radar",
                    label: this.$t("reviews.admin.btnShowCategory"),
                    iconClass: "fal fa-chart-pie",
                },
                {
                    id: "category-horizontalBar",
                    label: this.$t("reviews.admin.btnShowCategory"),
                    iconClass: "fal fa-chart-bar",
                },
                {
                    id: "timeline-list",
                    label: this.$t("reviews.admin.btnShowTimeline"),
                    iconClass: "fal fa-calendar",
                    visible: this.hasResults && this.serializedReportSource === "feedback",
                },
                {
                    label: this.$t("profile.feedback.btnShowSkillMatrix"),
                    href: `#/skill/matrix/${this.user._id}`,
                    visible: !!this.rights.P_MATRIX,
                },
            ];
        },
        chartType() {
            const allowedTypes = ["radar", "horizontalBar"];
            const type = this.selectedTab?.id?.split("-")?.[1];

            return allowedTypes.includes(type) ? type : "list";
        },
        chartData() {
            if (this.chartType === "list") {
                return (this.report?.results?.list || []).sort(
                    (skillA, skillB) =>
                        skillB.results?.primary?.avgP - skillA.results?.primary?.avgP,
                );
            }

            return {
                labels: this.report?.results?.graph?.labels || [],
                datasets: (this.report?.results?.graph?.results || []).map((result, index) => ({
                    label: this.chartSettings?.series?.[index] ?? "",
                    data: result,
                    backgroundColor: this.chartSettings?.backgroundColors?.[index],
                    borderColor: this.chartSettings?.borderColors?.[index],
                    pointBackgroundColor: this.chartSettings?.borderColors?.[index],
                    borderWidth: 2,
                    ...(this.chartSettings?.datasetOverride?.[index]
                        ? this.chartSettings.datasetOverride[index]
                        : {}),
                })),
            };
        },
        chartSettings() {
            return {
                backgroundColors: [
                    ...(this.serializedReportSource === "feedback"
                        ? ["rgba(93, 93, 233, 0.2)", "rgba(100, 194, 172, 0.2)"]
                        : this.serializedReportSource === "reviews"
                        ? [
                              "rgba(66,125,109, 0.05)",
                              "rgba(209,108,49, 0.05)",
                              "rgba(192,38,52, 0.05",
                              "rgba(67,23,192, 0.05)",
                              "rgba(34, 34, 34, 0.05)",
                          ]
                        : []),
                ],
                borderColors: [
                    ...(this.serializedReportSource === "feedback"
                        ? ["rgba(93, 93, 233, 0.8)", "rgba(100, 194, 172, 0.8)"]
                        : this.serializedReportSource === "reviews"
                        ? [
                              "rgba(66,125,109, 0.8)", // self
                              "rgba(209,108,49, 0.8)", // peers
                              "rgba(192,38,52, 0.8)", // direct reports
                              "rgba(67,23,192, 0.8)", // manager
                              "rgba(34, 34, 34, 0.8)", // total
                          ]
                        : []),
                ],
                series: [
                    ...(this.serializedReportSource === "feedback"
                        ? ["Self-Reflection", "Feedback"]
                        : this.serializedReportSource === "reviews"
                        ? ["Self", "Peers", "Direct R.", "Manager", "All"]
                        : []),
                ],
                datasetOverride: [
                    {
                        hidden: false,
                    },
                    {
                        hidden: false,
                    },
                    {
                        hidden: this.serializedReportSource === "reviews" ? false : true,
                        fill: false,
                    },
                    {
                        hidden: this.serializedReportSource === "reviews" ? false : true,
                    },
                    {
                        hidden: true,
                        fill: false,
                    },
                ],
                options: {
                    spanGaps: true,
                    responsive: true,
                    maintainAspectRatio: false,
                    fullWidth: true,
                    layout: {
                        padding: {
                            left: 20,
                            right: 20,
                            top: 20,
                            bottom: 20,
                        },
                    },
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: {
                            boxWidth: 15,
                            padding: 20,
                        },
                    },
                    animation: false,
                    elements: {
                        line: {
                            borderWidth: 3,
                        },
                        point: {
                            radius: 3,
                            pointStyle: "circle",
                        },
                    },
                    ...(this.chartType === "radar"
                        ? {
                              scale: {
                                  pointLabels: {
                                      callback: function(pointLabel) {
                                          if (pointLabel.length > 25)
                                              return pointLabel.substring(0, 24) + "...";
                                          else return pointLabel;
                                      },
                                  },
                                  ticks: {
                                      display: this.chartScaleType !== "100" ? true : false,
                                      min: 0,
                                      max: this.chartScaleType
                                          ? parseInt(this.chartScaleType, 10)
                                          : 5,
                                      beginAtZero: true,
                                      stepSize: this.chartScaleType !== "100" ? 1 : 100,
                                  },
                              },
                          }
                        : {
                              scales: {
                                  xAxes: [
                                      {
                                          display: false,
                                          ticks: {
                                              display: true,
                                              suggestedMax: this.chartScaleType
                                                  ? parseInt(this.chartScaleType, 10)
                                                  : 5,
                                              beginAtZero: true,
                                          },
                                      },
                                  ],
                              },
                          }),
                    tooltips: {
                        enabled: true,
                        mode: "label",
                        callbacks: {
                            title: function(tooltipItems, data) {
                                var idx = tooltipItems[0].index;
                                return data.labels[idx];
                            },
                            ...(this.chartScaleType === "100"
                                ? {
                                      label: function(tooltipItems, data) {
                                          return data.datasets[tooltipItems.datasetIndex].label;
                                      },
                                  }
                                : {}),
                        },
                    },
                },
            };
        },
        chartScaleType() {
            return this.report?.settings?.scale?.type;
        },
        hasResults() {
            return this.report?.noResults === false;
        },
        reportSourceOptions() {
            const options = [];

            if (this.currentUser.rights.F_STRUCTURED) {
                options.push({
                    text: this.$t("profile.feedback.optionShowFeedback"),
                    value: "feedback",
                });
            }

            if (this.currentUser.rights.R_VIEW) {
                options.push({
                    text: this.$t("profile.feedback.optionShowAllReviews"),
                    value: "reviews",
                });
            }

            for (const reviewCycle of this.reviewCycles) {
                options.push({ text: reviewCycle.name, value: reviewCycle._id });
            }

            return options;
        },
        serializedReportSource() {
            return ["feedback", "reviews"].includes(this.form.reportSource)
                ? this.form.reportSource
                : "reviews";
        },
        reportQuery() {
            const isForSingleReview =
                !["feedback", "reviews"].includes(this.form.reportSource) && this.form.reportSource;

            return {
                mode: "byParticipant",
                viewAs: this.isVisitor ? "owner" : "reviewee",
                format: "snapshot",
                export: false,
                source: this.serializedReportSource,
                filters: {
                    primary: {
                        users: [{ _id: this.user._id }],
                        managers: [],
                        localTeams: [],
                        perspectives: [],
                        skills: [],
                        categories: [],
                        reviewCycles: [
                            ...(isForSingleReview ? [{ _id: this.form.reportSource }] : []),
                        ],
                        periods: [],
                    },
                    secondary: {
                        users: [],
                        managers: [],
                        localTeams: [],
                        perspectives: [],
                        skills: [],
                        categories: [],
                        reviewCycles: [],
                        periods: [],
                    },
                },
                enableSecondaryFilters: false,
                groupBy: "localTeam",
                dimensionType: this.selectedTab?.id?.startsWith("category") ? "category" : "skill",
                dimensionShowDeactivated: isForSingleReview ? true : false,
                dimensionIncludeCustomQuestions: false,
                scatter: {
                    x: {},
                    y: {},
                },
                ignoreSafeguard: true,
                allowFallbackToFeedback: true,
            };
        },
    },
    watch: {
        reportQuery: {
            immediate: true,
            deep: true,
            async handler(newValue) {
                await this.loadData(newValue);
            },
        },
        serializedReportSource: {
            deep: true,
            handler(newValue) {
                if (this.selectedTab?.id === "timeline-list" && newValue !== "feedback") {
                    this.selectedTab = this.tabs[0];
                }
            },
        },
    },
    created() {
        if (this.currentUser.rights.R_VIEW) {
            this.form.reportSource = "reviews";
        } else {
            this.form.reportSource = "feedback";
        }
    },
    methods: {
        async onNavigateToReview({
            reviewCycleId,
            revieweeId,
            role = "manager",
            viewAs = "owner",
            entrypoint = "toDo",
        } = {}) {
            // get participant status
            const { data } = await api.get(
                `/api/reviewcycles/get/details/${reviewCycleId}/reviewee/${revieweeId}/${role}/${this.currentUser._id}/viewAs/${viewAs}`,
            );

            if (data.redirect) {
                this.$router.push(
                    `/reviews/update/${reviewCycleId}/reviewee/${revieweeId}/${data.redirect}/${this.currentUser._id}/viewAs/${data.redirect}/overview/${entrypoint}`,
                );
            } else {
                this.$router.push(
                    `/reviews/update/${reviewCycleId}/reviewee/${revieweeId}/${role}/${this.currentUser._id}/viewAs/${viewAs}/overview/${entrypoint}`,
                );
            }
        },
        async loadData(filters = {}) {
            // wait for data to be set
            if (!this.form.reportSource) return;

            this.isLoading = true;

            try {
                const { data } = await api.post("/api/stats/get/report", filters);
                this.report = data;

                // set and keep data once available as not all report request payloads contain that data
                if (
                    Array.isArray(this.report?.settings?.reviewCyclesWithAccess) &&
                    this.report.settings.reviewCyclesWithAccess.length &&
                    this.form.reportSource === "reviews"
                ) {
                    this.reviewCycles = this.report.settings.reviewCyclesWithAccess;
                }
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>
